import { type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query';

import * as Api from '@ViewModels';
import { type IPhoneCall } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type LeadCall = {
	id?: string;
	timestamp?: string;
	/** Display name of the rep who placed the call. */
	rep?: string;
	/** Outcome label, read off the server-rendered summary. */
	outcome?: string;
	/** An outcome saved with no tracked dial. Carries no number, duration, recording or transcript. */
	loggedManually?: boolean;
	phoneCall?: IPhoneCall;
};

type PhoneCallTimelineEvent = Api.ITimelineEvent & { phoneCall?: IPhoneCall };

const CALL_EVENT_TYPES = ['PhoneCallCompletedEvent', 'UntrackedPhoneCallEvent'].join(',');
const PAGE_SIZE = 25;

/**
 * The resolver renders each call as "<rep> had a phone call with <contact> <number>
 * (<outcome>)", so the outcome is only available as this trailing parenthetical until
 * the event exposes its interaction structurally.
 */
export function parseOutcome(summary: string | undefined): string | undefined {
	const match = summary?.match(/\(([^()]*)\)\s*$/);
	return match?.[1]?.trim() || undefined;
}

/**
 * Only `PhoneCallCompletedEvent` carries a resolved `phoneCall` — the `UntrackedPhoneCallEvent` the
 * API writes for an outcome saved without a tracked dial has none, so every field falls back to the event.
 */
export function toLeadCall(event: PhoneCallTimelineEvent): LeadCall {
	return {
		id: event.phoneCall?.id ?? event.id,
		timestamp: event.phoneCall?.startTime ?? event.phoneCall?.creationDate ?? event.timestamp ?? event.creationDate,
		rep: event.originalCreator,
		outcome: parseOutcome(event.summary) ?? parseOutcome(event.title),
		loggedManually: event._type === 'UntrackedPhoneCallEvent',
		phoneCall: event.phoneCall,
	};
}

/**
 * Every call logged against a lead, by any rep. Uses the company timeline rather than
 * `phonecall/filter`, which rejects a request that does not name a single user and so can
 * never answer "what has this lead been tried on".
 */
export function useLeadCallHistory(companyId: string | undefined): UseInfiniteQueryResult<LeadCall[], Error> {
	const userSession = useUserSession();

	return useInfiniteQuery({
		queryKey: queryKeys.focusedView.leadCallHistory(companyId),
		enabled: !!companyId,
		initialPageParam: '',
		queryFn: ({ pageParam }: { pageParam: string }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<PhoneCallTimelineEvent>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `timeline/Company/${companyId}`,
					queryParams: { typeOf: CALL_EVENT_TYPES, pageSize: String(PAGE_SIZE), pageToken: pageParam },
				}),
				'GET'
			),
		getNextPageParam: (lastPage: Api.IPagedCollection<PhoneCallTimelineEvent>, _pages: unknown, lastParam: string) =>
			lastPage.pageToken && lastPage.pageToken !== lastParam ? lastPage.pageToken : undefined,
		select: data => {
			const rows = data.pages.flatMap(page => (page.values ?? []).map(toLeadCall));
			const seen = new Set<string>();
			return rows
				.filter(row => {
					if (!row.id) return true;
					if (seen.has(row.id)) return false;
					seen.add(row.id);
					return true;
				})
				.sort((a, b) => (b.timestamp ? Date.parse(b.timestamp) : 0) - (a.timestamp ? Date.parse(a.timestamp) : 0));
		},
		retry: false,
		staleTime: 60 * 1000,
	});
}
